import { Search } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { queryStringToObject } from "@outschool/routes";
import { dayjs } from "@outschool/time";

import { AvailableTime } from "../AvailableTimesInput/types";
import { TimeOfDayOption, checkIsSelected } from "../AvailableTimesInput/utils";
import {
  ClassSizeLearnerAttribute,
  LearnerAttributesType,
  LearningFormatSearchTerms,
  SOURCE_MAP,
  TIME_OF_DAY_OPTIONS,
} from "./consts";

export function getSourceFromQueryParams() {
  const queries = queryStringToObject(window.location.search);
  const foundEntry = Object.entries(SOURCE_MAP).find(([key]) =>
    Boolean(queries[key]),
  );

  if (!foundEntry) {
    return null;
  }

  const [key, mappedValue] = foundEntry;

  // For variant, return the actual query parameter value instead of the mapped value
  if (key === "variant") {
    return queries[key] || null;
  }

  return mappedValue;
}

export const findSelectedTimeOptionFromAvailableTimes = (
  options: TimeOfDayOption[],
  av: AvailableTime[],
) => {
  return options
    .filter(tod => tod.range[0] && tod.range[1])
    .find(tod => checkIsSelected(av, tod.range[0]!, tod.range[1]!, tod.days));
};

export const onChangeMultiSelect = (
  selectedValues: string[],
  changedValue: string,
  onChange: (values: string[]) => void,
) => {
  const newSelectedValues = selectedValues.includes(changedValue)
    ? selectedValues.filter(selectedValue => selectedValue !== changedValue)
    : [...selectedValues, changedValue];
  onChange(newSelectedValues);
  return newSelectedValues;
};

export const getAgeFromBirthMonthAndYear = (month: number, year: number) => {
  const firstDayOfBirthMonth = dayjs().year(year).month(month).startOf("month");
  return Number(dayjs().diff(firstDayOfBirthMonth, "year"));
};

// Helper function to convert onboarding snake_case class sizes to PascalCase for utility functions
const mapToSearchClassSizes = (
  classSizes: ClassSizeLearnerAttribute[],
): Search.ClassSize[] => {
  return classSizes.flatMap(size => {
    switch (size) {
      case ClassSizeLearnerAttribute.Group:
        return ["SmallGroup", "LargeGroup"];
      case ClassSizeLearnerAttribute.OneOnOne:
        return ["OneOnOne"];
      case ClassSizeLearnerAttribute.SmallGroup:
        return ["SmallGroup"];
      case ClassSizeLearnerAttribute.LargeGroup:
        return ["LargeGroup"];
      default:
        return [];
    }
  });
};

export const deriveSearchFiltersFromAttributes = (
  learnerAttributes: LearnerAttributesType,
): SearchFiltersType => {
  const {
    birthMonth,
    birthYear,
    subjects,
    learningFormats,
    classSizes,
    availableTimes,
  } = learnerAttributes;

  const age =
    !!birthMonth && !!birthYear
      ? getAgeFromBirthMonthAndYear(birthMonth, birthYear).toString()
      : null;
  const theme = subjects?.join(", ") || null;
  const format =
    learningFormats?.map(value => LearningFormatSearchTerms[value]).join(",") ||
    null;

  // Map legacy class sizes to new format
  const mappedClassSizes = classSizes ? mapToSearchClassSizes(classSizes) : [];
  const capacityFilters =
    Search.getCapacityFiltersFromClassSize(mappedClassSizes);

  const capacityMax = capacityFilters.capacityMax;
  const capacityMin = capacityFilters.capacityMin;

  let startAfterTime = null;
  let endByTime = null;
  let dow = null;
  if (!!availableTimes?.length) {
    const matchedOption = findSelectedTimeOptionFromAvailableTimes(
      TIME_OF_DAY_OPTIONS,
      availableTimes,
    );
    // only populate time of day for preset availabilty options, not custom schedules
    if (matchedOption) {
      startAfterTime = matchedOption.range?.[0] || null;
      endByTime = matchedOption.range?.[1] || null;
    }

    const daysOfWeekWithAvailability = [
      ...new Set(
        availableTimes.map(
          (availability: AvailableTime) =>
            Search.DAYS_OF_WEEK[availability.day],
        ),
      ),
    ];
    // include any days of the week with availability
    if (daysOfWeekWithAvailability.length) {
      dow = daysOfWeekWithAvailability.join(",");
    }
  }

  return {
    age,
    theme,
    format,
    capacityMax,
    capacityMin,
    startAfterTime,
    endByTime,
    dow,
  };
};
